import React, { useCallback, useState } from 'react'
import { EnhancedProvider } from 'containers/Scheduling/types'
import { Logger } from 'utils/logger.utils'
import { createDateKey } from './utils'
import { calculateSlotPhaseData } from './slotPhaseCalculator'
import { SlotRenderer } from './slotRenderer'
import { UseProviderTimeSlotsProps, ExpandedProviders } from './types'

export const useProviderTimeSlots = ({
  selectedDate,
  timeZone,
  providersForSelectedDate,
  onSlotClick,
  chipState,
}: UseProviderTimeSlotsProps): {
  renderProviderTimeSlots: (provider: EnhancedProvider) => JSX.Element
  expandedProviders: ExpandedProviders
  setExpandedProviders: React.Dispatch<React.SetStateAction<ExpandedProviders>>
} => {
  const [expandedProviders, setExpandedProviders] = useState<ExpandedProviders>(
    {}
  )
  const logger = new Logger('useProviderTimeSlots')

  const dateKey = createDateKey(selectedDate, timeZone)

  const handleExpandClick = useCallback(
    (providerId: string, nextPhase: number) => {
      setExpandedProviders((prev) => {
        const newState = { ...prev }

        if (!newState[dateKey]) {
          newState[dateKey] = {}
        }

        newState[dateKey][providerId] = nextPhase
        return newState
      })
    },
    [dateKey]
  )

  const renderProviderTimeSlots = useCallback(
    (provider: EnhancedProvider) => {
      const currentPhase = expandedProviders[dateKey]?.[provider.id] || 1

      const slotPhaseData = calculateSlotPhaseData(
        provider,
        currentPhase,
        providersForSelectedDate,
        logger,
        timeZone
      )

      return (
        <SlotRenderer
          slotPhaseData={slotPhaseData}
          provider={provider}
          timeZone={timeZone}
          chipState={chipState}
          onSlotClick={onSlotClick}
          onExpandClick={handleExpandClick}
        />
      )
    },
    [
      expandedProviders,
      dateKey,
      providersForSelectedDate,
      timeZone,
      chipState,
      onSlotClick,
      handleExpandClick,
      logger,
    ]
  )

  return {
    renderProviderTimeSlots,
    expandedProviders,
    setExpandedProviders,
  }
}

// Re-export types for convenience
export type { UseProviderTimeSlotsProps, ExpandedProviders } from './types'
